// src/Dashboard/pages/user/Galleries/components/GalleryCard.jsx

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createDOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
  FaEye,
  FaPause,
  FaPen,
  FaPlay,
  FaTrash,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../services/albumPhotoServices";

import {
  FALLBACK_IMAGE,
  getCoverPhoto,
  onImgError,
  sortPhotos,
  stripHtml,
} from "../utils/galleriesUtils";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const DESCRIPTION_COLLAPSED_HEIGHT = 130;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "dashboard-gallery-card-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-7 text-slate-600",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-[#013E8E]",
  "hover:prose-a:underline",
  "dark:prose-a:text-indigo-300",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-[#013E8E]",
  "prose-blockquote:bg-blue-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-blue-500/10",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Médias masqués dans l’aperçu
  "[&_img]:hidden",
  "[&_iframe]:hidden",
  "[&_video]:hidden",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function GalleryCard({
  gallery,
  isManager,
  onEdit,
  onDelete,
}) {
  const navigate = useNavigate();

  const [isPlaying, setIsPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [shouldClamp, setShouldClamp] = useState(false);

  const descriptionRef = useRef(null);

  const orderedPhotos = useMemo(() => {
    return sortPhotos(gallery?.photos || []);
  }, [gallery?.photos]);

  const coverPhoto = useMemo(() => {
    return getCoverPhoto(orderedPhotos);
  }, [orderedPhotos]);

  const cover =
    toAbsolutePhotoUrl(coverPhoto?.photo_url || "") ||
    FALLBACK_IMAGE;

  const hasMany = orderedPhotos.length > 1;

  const descriptionText = useMemo(() => {
    return stripHtml(gallery?.description || "");
  }, [gallery?.description]);

  const safeHtml = useMemo(() => {
    if (!descriptionText) return "";

    return sanitizeHtml(gallery?.description || "");
  }, [descriptionText, gallery?.description]);

  /*
   * Réinitialise les états lorsque la galerie change.
   */
  useEffect(() => {
    setExpanded(false);
    setIsPlaying(false);
  }, [gallery?.id]);

  /*
   * Mesure la hauteur réelle du contenu enrichi.
   */
  useLayoutEffect(() => {
    const element = descriptionRef.current;

    if (!element || !safeHtml) {
      setShouldClamp(false);
      return undefined;
    }

    let animationFrameId;

    const checkHeight = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        setShouldClamp(
          element.scrollHeight >
            DESCRIPTION_COLLAPSED_HEIGHT + 2,
        );
      });
    };

    checkHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkHeight)
        : null;

    resizeObserver?.observe(element);
    window.addEventListener("resize", checkHeight);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkHeight);
    };
  }, [safeHtml, gallery?.id, expanded]);

  const sliderSettings = {
    dots: true,
    infinite: orderedPhotos.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    lazyLoad: "ondemand",
    pauseOnHover: true,
    adaptiveHeight: false,
  };

  const handleOpenAlbum = () => {
    navigate(
      `/dashboard/galerie-photos/album/${gallery.id}`,
    );
  };

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950">
      {/* Couverture ou diaporama */}

      <div className="relative">
        {!isPlaying || !hasMany ? (
          <button
            type="button"
            onClick={handleOpenAlbum}
            className="block w-full bg-slate-100 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-[#013E8E]/40 dark:bg-slate-900"
            title="Ouvrir l’album"
            aria-label={`Ouvrir l’album ${
              gallery?.titre || "Sans titre"
            }`}
          >
            <div
              className="w-full"
              style={{ aspectRatio: "16 / 9" }}
            >
              <img
                src={cover}
                alt={gallery?.titre || "Album"}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                loading="lazy"
                onError={onImgError}
              />
            </div>
          </button>
        ) : (
          <Slider {...sliderSettings}>
            {orderedPhotos.map((photo, index) => (
              <div
                key={`${gallery.id}-photo-${
                  photo.id || index
                }`}
                className="w-full bg-slate-100 dark:bg-slate-900"
              >
                <div
                  className="w-full"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  <img
                    src={
                      toAbsolutePhotoUrl(
                        photo.photo_url,
                      ) || FALLBACK_IMAGE
                    }
                    alt={`${
                      gallery?.titre || "Album"
                    } - photo ${index + 1}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={onImgError}
                  />
                </div>
              </div>
            ))}
          </Slider>
        )}

        {/* Badges */}

        <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-2">
          <span className="inline-flex rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white shadow-sm">
            {orderedPhotos.length} photo
            {orderedPhotos.length > 1 ? "s" : ""}
          </span>

          {!gallery?.is_active ? (
            <span className="inline-flex rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
              Masqué
            </span>
          ) : null}
        </div>

        {/* Lecture du diaporama */}

        {hasMany ? (
          <button
            type="button"
            onClick={() =>
              setIsPlaying((value) => !value)
            }
            title={
              isPlaying
                ? "Mettre en pause"
                : "Lancer le diaporama"
            }
            className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#013E8E]/30 dark:bg-slate-950/90 dark:text-slate-200 dark:ring-slate-700"
            aria-label={
              isPlaying
                ? "Mettre le diaporama en pause"
                : "Lancer le diaporama"
            }
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
        ) : null}
      </div>

      {/* Corps de la carte */}

      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-1 flex-col">
          <h3 className="line-clamp-2 text-base font-bold text-slate-900 dark:text-white">
            {gallery?.titre || "Sans titre"}
          </h3>

          {/* Description enrichie */}

          {safeHtml ? (
            <div className="relative mt-3">
              <div
                ref={descriptionRef}
                className={cn(
                  RICH_TEXT_CLASSES,
                  "transition-[max-height] duration-300",
                  expanded
                    ? "max-h-[600px] overflow-y-auto pr-2"
                    : "max-h-[130px] overflow-hidden pr-1",
                )}
                style={{ tabSize: 4 }}
                dangerouslySetInnerHTML={{
                  __html: safeHtml,
                }}
              />

              {!expanded && shouldClamp ? (
                <div
                  className={cn(
                    "pointer-events-none absolute inset-x-0 bottom-0 h-16",
                    "bg-gradient-to-t from-white via-white/95 to-transparent",
                    "dark:from-slate-950 dark:via-slate-950/95",
                  )}
                  aria-hidden="true"
                />
              ) : null}

              {shouldClamp ? (
                <button
                  type="button"
                  onClick={() =>
                    setExpanded((value) => !value)
                  }
                  className="relative z-10 mt-2 text-xs font-bold text-[#013E8E] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#013E8E]/30 dark:text-indigo-300"
                  aria-expanded={expanded}
                >
                  {expanded ? "Réduire" : "Voir plus"}
                </button>
              ) : null}
            </div>
          ) : (
            <p className="mt-3 text-sm italic leading-7 text-slate-500 dark:text-slate-400">
              Pas de description.
            </p>
          )}
        </div>

        {/* Actions */}

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <button
            type="button"
            onClick={handleOpenAlbum}
            className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-800"
          >
            <FaEye />
            Voir l’album
          </button>

          {isManager ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onEdit}
                title="Modifier"
                aria-label="Modifier"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaPen />
              </button>

              <button
                type="button"
                onClick={onDelete}
                title="Supprimer"
                aria-label="Supprimer"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
              >
                <FaTrash />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}